import { clearFeatureFlagCache } from "@/contexts/FeatureFlagContext"
import { amplitude } from "@/services/analytics/amplitude"
import { setUserContext } from "@/utils/metadata"
import { resetUpgradeSessionState } from "@/utils/upgradeSession"
import * as Sentry from "@sentry/react"
import type { QueryClient } from "@tanstack/react-query"
import { signOut } from "aws-amplify/auth"
import {
  beginSignOut,
  failSignOut,
  finishSignOut,
  isSignOutActive,
} from "./authRequestLifecycle"

/**
 * - `signed-out`: credentials are gone and the session's cached data and identity are cleared.
 * - `failed`: sign-out was rejected; requests work again and the caller should tell the user.
 * - `skipped`: another sign-out is already running or done; nothing was started.
 */
export type SignOutOutcome = "signed-out" | "failed" | "skipped"

// Once sign-out has resolved the credentials are gone, so a cleanup step that throws must not leave
// the steps after it undone: each one runs on its own.
const bestEffort = (step: () => void): void => {
  try {
    step()
  } catch {
    // Never restore a signed-out session because cleanup failed.
  }
}

/**
 * The one sign-out sequence, for every place that signs the user out: it rejects in-flight requests
 * first (see authRequestLifecycle), signs out, then clears everything the next person to sign in in
 * this tab must not inherit.
 *
 * `onSignedOut` runs right after sign-out succeeds, before any cleanup, for state the caller owns
 * (e.g. the hook's own `user`). Toasts and navigation stay with the caller, which knows its surface.
 */
export async function performSignOut(
  queryClient: QueryClient,
  onSignedOut?: () => void,
): Promise<SignOutOutcome> {
  if (isSignOutActive()) return "skipped"
  beginSignOut()
  try {
    await queryClient.cancelQueries()
    await signOut()
  } catch {
    failSignOut()
    return "failed"
  }
  finishSignOut()
  onSignedOut?.()

  // First: the next person to sign in in this tab must not see this session's capabilities.
  bestEffort(clearFeatureFlagCache)
  // The upgrade link flag is per tab and the refresh answers are per page load: a
  // different user signing in here must not refresh on the last one's payment.
  bestEffort(resetUpgradeSessionState)
  // `queryClient` lives for the whole app/tab lifetime (index.tsx) and no query key is scoped by user
  // id, so without this a different user signing in in the same tab could render this session's
  // cached "my own" data for a moment before the refetch corrects it, e.g. another user's personal
  // signature images.
  bestEffort(() => queryClient.clear())
  bestEffort(() => setUserContext(null))
  bestEffort(() => {
    Sentry.setUser(null)
    Sentry.setTag("organization_id", "")
  })
  bestEffort(() => {
    const identifyObj = new amplitude.Identify()
    identifyObj.set("organization_id", "")
    identifyObj.set("groups", [])
    amplitude.identify(identifyObj)
    amplitude.reset()
  })
  return "signed-out"
}
